import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from './Button';

/**
 * Module-level error recovery.
 *
 * Wrapped around each routed page rather than the whole app: a rendering bug in the
 * expense analytics chart should not take the sidebar and the notification bell with it,
 * because the user still needs to be able to navigate away.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; label?: string },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Render error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="page-container">
        <div className="surface-card p-6 text-center" data-testid="error-boundary">
          <h2 className="text-section font-semibold text-content">
            {this.props.label ?? 'This screen'} could not be displayed
          </h2>
          <p className="mx-auto mt-1 max-w-prose text-pretty text-body text-content-secondary">
            Something in the page failed while rendering. The rest of the application is still working — use
            the navigation to go elsewhere, or try again.
          </p>
          <p className="mt-2 text-body text-content-secondary">
            <code className="code-ref">{error.message}</code>
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button onClick={() => this.setState({ error: null })}>Try again</Button>
            <Button variant="primary" onClick={() => window.location.assign('/')}>
              Back to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
